import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const SuperadminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerDetails, setProviderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showExtendModal, setShowExtendModal] = useState(null);
  const [extendDays, setExtendDays] = useState(365);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("superadmin_token");
    return {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    };
  };

  useEffect(() => {
    const token = localStorage.getItem("superadmin_token");
    if (!token) {
      navigate("/sad/login");
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, providersRes] = await Promise.all([
        fetch(`${API_URL}/api/sad/stats`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/sad/providers`, { headers: getAuthHeaders() })
      ]);

      if (statsRes.status === 401 || providersRes.status === 401) {
        localStorage.removeItem("superadmin_token");
        navigate("/sad/login");
        return;
      }

      const statsData = await statsRes.json();
      const providersData = await providersRes.json();

      setStats(statsData);
      setProviders(providersData);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchProviderDetails = async (providerId) => {
    try {
      const res = await fetch(`${API_URL}/api/sad/providers/${providerId}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setProviderDetails(data);
      setSelectedProvider(providerId);
    } catch (error) {
      toast.error("Failed to load provider details");
    }
  };

  const handleSuspendProvider = async (providerId, suspend) => {
    try {
      await fetch(`${API_URL}/api/sad/providers/${providerId}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_suspended: suspend })
      });
      toast.success(suspend ? "Provider suspended" : "Provider activated");
      fetchData();
      if (selectedProvider === providerId) {
        fetchProviderDetails(providerId);
      }
    } catch (error) {
      toast.error("Failed to update provider");
    }
  };

  const handleExtendSubscription = async (providerId, deviceId) => {
    try {
      await fetch(`${API_URL}/api/sad/subscriptions/extend`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          provider_id: providerId,
          device_id: deviceId,
          duration_days: extendDays
        })
      });
      toast.success(`Subscription extended by ${extendDays} days`);
      setShowExtendModal(null);
      if (selectedProvider) {
        fetchProviderDetails(selectedProvider);
      }
    } catch (error) {
      toast.error("Failed to extend subscription");
    }
  };

  const handleCancelSubscription = async (deviceId) => {
    if (!window.confirm("Are you sure you want to cancel this subscription?")) return;
    
    try {
      await fetch(`${API_URL}/api/sad/subscriptions/cancel?device_id=${deviceId}`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      toast.success("Subscription cancelled");
      if (selectedProvider) {
        fetchProviderDetails(selectedProvider);
      }
    } catch (error) {
      toast.error("Failed to cancel subscription");
    }
  };

  const handleDeleteProvider = async (providerId) => {
    if (!window.confirm("Are you sure? This will delete the provider and ALL their data!")) return;
    
    try {
      await fetch(`${API_URL}/api/sad/providers/${providerId}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      toast.success("Provider deleted");
      setSelectedProvider(null);
      setProviderDetails(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete provider");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("superadmin_token");
    localStorage.removeItem("superadmin_username");
    navigate("/sad/login");
  };

  const filteredProviders = providers.filter(p => 
    p.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.company_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return true;
    return new Date(dateStr) < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Superadmin Panel</h1>
              <p className="text-slate-400 text-sm">STB Emulator Management</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Logout
          </button>
        </div>
      </header>

      <div className="p-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Providers</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.total_providers}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Devices</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.total_devices}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Active Subscriptions</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.active_subscriptions}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Revenue (This Month)</p>
                  <p className="text-3xl font-bold text-white mt-1">£{stats.revenue_this_month.toFixed(2)}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <p className="text-slate-500 text-sm mt-2">Total: £{stats.total_revenue.toFixed(2)}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Providers List */}
          <div className="lg:col-span-1 bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white mb-3">Providers</h2>
              <input
                type="text"
                placeholder="Search providers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {filteredProviders.map((provider) => (
                <div
                  key={provider.id}
                  onClick={() => fetchProviderDetails(provider.id)}
                  className={`p-4 border-b border-slate-700 cursor-pointer hover:bg-slate-700/50 transition-colors ${
                    selectedProvider === provider.id ? "bg-slate-700/50" : ""
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-medium text-white truncate">{provider.company_name}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      provider.subscription_status === "active" 
                        ? "bg-green-500/20 text-green-400"
                        : provider.subscription_status === "trial"
                        ? "bg-yellow-500/20 text-yellow-400"
                        : "bg-red-500/20 text-red-400"
                    }`}>
                      {provider.subscription_status}
                    </span>
                  </div>
                  <p className="text-slate-400 text-sm truncate">{provider.email}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                    <span>{provider.total_devices} devices</span>
                    <span>{provider.active_devices} active</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Provider Details */}
          <div className="lg:col-span-2 bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            {providerDetails ? (
              <div>
                <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-white">{providerDetails.provider.company_name}</h2>
                    <p className="text-slate-400 text-sm">{providerDetails.provider.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSuspendProvider(
                        providerDetails.provider.id, 
                        !providerDetails.provider.is_suspended
                      )}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        providerDetails.provider.is_suspended
                          ? "bg-green-600 hover:bg-green-700 text-white"
                          : "bg-yellow-600 hover:bg-yellow-700 text-white"
                      }`}
                    >
                      {providerDetails.provider.is_suspended ? "Activate" : "Suspend"}
                    </button>
                    <button
                      onClick={() => handleDeleteProvider(providerDetails.provider.id)}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Devices */}
                <div className="p-4">
                  <h3 className="text-md font-semibold text-white mb-3">Devices ({providerDetails.devices.length})</h3>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {providerDetails.devices.map((device) => (
                      <div key={device.id} className="bg-slate-700/50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="font-medium text-white">{device.name}</span>
                            <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                              device.status === "active" 
                                ? "bg-green-500/20 text-green-400"
                                : "bg-red-500/20 text-red-400"
                            }`}>
                              {device.status}
                            </span>
                            {device.is_trial && (
                              <span className="ml-2 px-2 py-0.5 rounded text-xs bg-yellow-500/20 text-yellow-400">
                                Trial
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setShowExtendModal(device)}
                              className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs"
                            >
                              Extend
                            </button>
                            <button
                              onClick={() => handleCancelSubscription(device.id)}
                              className="px-2 py-1 bg-red-600/50 hover:bg-red-600 text-white rounded text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                        <div className="text-sm text-slate-400 space-y-1">
                          <p>MAC: <span className="font-mono text-slate-300">{device.mac_address}</span></p>
                          <p>Code: <span className="font-mono text-slate-300">{device.access_code}</span></p>
                          <p>Expires: <span className={isExpired(device.subscription_expires) ? "text-red-400" : "text-green-400"}>
                            {formatDate(device.subscription_expires)}
                          </span></p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Payments */}
                <div className="p-4 border-t border-slate-700">
                  <h3 className="text-md font-semibold text-white mb-3">Recent Payments</h3>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {providerDetails.payments.length === 0 ? (
                      <p className="text-slate-400 text-sm">No payments yet</p>
                    ) : (
                      providerDetails.payments.map((payment) => (
                        <div key={payment.id} className="flex items-center justify-between text-sm">
                          <span className="text-slate-300">{formatDate(payment.created_at)}</span>
                          <span className="text-white font-medium">£{payment.amount.toFixed(2)}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            payment.payment_status === "paid"
                              ? "bg-green-500/20 text-green-400"
                              : "bg-yellow-500/20 text-yellow-400"
                          }`}>
                            {payment.payment_status}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-[500px] flex items-center justify-center text-slate-400">
                Select a provider to view details
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Extend Subscription Modal */}
      {showExtendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-4">
              Extend Subscription
            </h3>
            <p className="text-slate-400 mb-4">
              Device: {showExtendModal.name}
            </p>
            <div className="mb-4">
              <label className="block text-sm text-slate-300 mb-2">Days to extend</label>
              <select
                value={extendDays}
                onChange={(e) => setExtendDays(Number(e.target.value))}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              >
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
                <option value={180}>180 days</option>
                <option value={365}>1 year (365 days)</option>
              </select>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowExtendModal(null)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => handleExtendSubscription(providerDetails.provider.id, showExtendModal.id)}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
              >
                Extend
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperadminDashboard;
